/**
 * Question Form Dialog
 * 
 * Create/Edit feedback questions with dynamic configuration based on type.
 */

import React, { useState, useEffect } from 'react'
import { X, Plus, Trash2, HelpCircle } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Checkbox } from '@/components/ui/checkbox'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import questionBankService, {
  Question,
  QuestionCreate,
  QuestionType,
  QuestionCategory,
  QuestionConfig,
  QUESTION_CATEGORIES,
} from '@/services/api/questionBankService'

interface QuestionFormDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  question?: Question
  onSuccess: () => void
}

const QUESTION_TYPES: { value: QuestionType; label: string; description: string }[] = [
  { value: 'TEXT', label: 'Open Text', description: 'Free-form text response' },
  { value: 'RATING_SCALE', label: 'Rating Scale', description: 'Numeric rating (e.g., 1-5)' },
  { value: 'LIKERT', label: 'Likert Scale', description: 'Agreement scale (Strongly Disagree to Strongly Agree)' },
  { value: 'MULTIPLE_CHOICE', label: 'Multiple Choice', description: 'Single selection from options' },
  { value: 'MULTI_SELECT', label: 'Multi-Select', description: 'Multiple selections allowed' },
  { value: 'YES_NO', label: 'Yes/No', description: 'Binary response' },
  { value: 'RANKING', label: 'Ranking', description: 'Rank options in order' },
]

// The same labels respondents see as section headings
const CATEGORIES = QUESTION_CATEGORIES

const FEEDBACK_CONTEXTS = [
  { value: 'matrix', label: 'Matrix Feedback' },
  { value: 'upward_360', label: 'Upward 360°' },
  { value: 'employee_requested', label: 'Team Member Requested' },
  { value: 'peer', label: 'Peer Feedback' },
  { value: 'project', label: 'Project Feedback' },
]

const JOB_FAMILIES = [
  'ALL',
  'ACADEMIC',
  'ACADEMIC_MGMT',
  'CAMPUS_MGMT',
  'ADMIN',
  'FINANCE',
  'IT',
  'HR',
  'STUDENT_AFFAIRS',
]

const QuestionFormDialog: React.FC<QuestionFormDialogProps> = ({
  open,
  onOpenChange,
  question,
  onSuccess,
}) => {
  const [loading, setLoading] = useState(false)
  const [formData, setFormData] = useState<Partial<QuestionCreate>>({
    question_text: '',
    question_code: '',
    question_type: 'TEXT',
    category: 'COMMUNICATION',
    applicable_job_families: ['ALL'],
    applicable_feedback_contexts: [],
    is_required: false,
  })

  // Rating scale config
  const [ratingMin, setRatingMin] = useState(1)
  const [ratingMax, setRatingMax] = useState(5)
  const [ratingLabels, setRatingLabels] = useState<Record<string, string>>({})

  // Likert/Multiple choice config
  const [options, setOptions] = useState<string[]>([])
  const [newOption, setNewOption] = useState('')

  // Multi-select config
  const [minSelections, setMinSelections] = useState(1)
  const [maxSelections, setMaxSelections] = useState<number | undefined>(undefined)

  useEffect(() => {
    // Only run when dialog opens
    if (!open) return

    if (question) {
      if (import.meta.env.DEV) console.log('📝 Loading question for edit:', question)
      
      // Convert API values (lowercase) to UI values (uppercase)
      const questionType = (question.question_type || 'TEXT').toUpperCase() as QuestionType
      const category = (question.category || 'COMMUNICATION').toUpperCase() as QuestionCategory
      
      // Load existing question for editing
      setFormData({
        question_text: question.question_text || '',
        question_code: question.question_code || '',
        question_type: questionType,
        category: category,
        applicable_job_families: question.applicable_job_families || ['ALL'],
        applicable_feedback_contexts: question.applicable_feedback_contexts || [],
        is_required: question.is_required || false,
        helper_text: question.helper_text || '',
        admin_notes: question.admin_notes || '',
      })
      if (import.meta.env.DEV) console.log('✅ Form data set (converted to uppercase):', {
        type: questionType,
        category: category,
        config: question.question_config
      })

      // Reset config states first
      setRatingMin(1)
      setRatingMax(5)
      setRatingLabels({})
      setOptions([])
      setMinSelections(1)
      setMaxSelections(undefined)

      // Load config if present
      if (question.question_config) {
        const config = question.question_config
        if (import.meta.env.DEV) console.log('📋 Loading config for type:', questionType, config)
        
        if (questionType === 'RATING_SCALE') {
          setRatingMin(config.min || 1)
          setRatingMax(config.max || 5)
          setRatingLabels(config.labels || {})
          if (import.meta.env.DEV) console.log('✅ Rating scale config loaded:', { min: config.min, max: config.max })
        } else if (['LIKERT', 'MULTIPLE_CHOICE', 'RANKING'].includes(questionType)) {
          setOptions(config.options || [])
          if (import.meta.env.DEV) console.log('✅ Options loaded:', config.options?.length, 'options')
        } else if (questionType === 'MULTI_SELECT') {
          setOptions(config.options || [])
          setMinSelections(config.min_selections ?? 1)
          setMaxSelections(config.max_selections)
          if (import.meta.env.DEV) console.log('✅ Multi-select config loaded:', { 
            options: config.options?.length, 
            min: config.min_selections, 
            max: config.max_selections 
          })
        }
      }
    } else {
      // New question - reset to defaults
      resetForm()
    }
  }, [question, open])

  const resetForm = () => {
    setFormData({
      question_text: '',
      question_code: '',
      question_type: 'TEXT',
      category: 'COMMUNICATION',
      applicable_job_families: ['ALL'],
      applicable_feedback_contexts: [],
      is_required: false,
    })
    setRatingMin(1)
    setRatingMax(5)
    setRatingLabels({})
    setOptions([])
    setNewOption('')
    setMinSelections(1)
    setMaxSelections(undefined)
  }

  const buildQuestionConfig = (): QuestionConfig | undefined => {
    if (formData.question_type === 'RATING_SCALE') {
      return {
        min: ratingMin,
        max: ratingMax,
        labels: ratingLabels,
      }
    } else if (['LIKERT', 'MULTIPLE_CHOICE', 'RANKING'].includes(formData.question_type!)) {
      return { options }
    } else if (formData.question_type === 'MULTI_SELECT') {
      return {
        options,
        min_selections: minSelections,
        max_selections: maxSelections,
      }
    }
    return undefined
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)

    try {
      // Convert UI values (uppercase) back to API values (lowercase).
      // Strip an empty `question_code` on create so the backend's
      // auto-generator (`{CATEGORY}_Q{NN}`) takes over. Pydantic's
      // min_length=3 would otherwise reject the empty string.
      const { question_code: rawCode, ...rest } = formData
      const questionData: QuestionCreate = {
        ...rest,
        question_type: formData.question_type?.toLowerCase() as any,
        category: formData.category?.toLowerCase() as any,
        question_config: buildQuestionConfig(),
        // Send the existing code on edit so the backend leaves it alone;
        // omit on create to trigger auto-gen.
        ...(question && rawCode ? { question_code: rawCode } : {}),
      } as QuestionCreate

      if (import.meta.env.DEV) console.log('💾 Saving question with lowercase values:', questionData)

      if (question) {
        await questionBankService.updateQuestion(question.id, questionData)
      } else {
        await questionBankService.createQuestion(questionData)
      }

      onSuccess()
      onOpenChange(false)
      resetForm()
    } catch (error) {
      console.error('Error saving question:', error)
      alert('Error saving question. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const addOption = () => {
    if (newOption.trim()) {
      setOptions([...options, newOption.trim()])
      setNewOption('')
    }
  }

  const removeOption = (index: number) => {
    setOptions(options.filter((_, i) => i !== index))
  }

  const toggleContext = (context: string) => {
    const current = formData.applicable_feedback_contexts || []
    if (current.includes(context)) {
      setFormData({
        ...formData,
        applicable_feedback_contexts: current.filter(c => c !== context),
      })
    } else {
      setFormData({
        ...formData,
        applicable_feedback_contexts: [...current, context],
      })
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" key={question?.id || 'new'}>
        <DialogHeader>
          <DialogTitle>{question ? 'Edit Question' : 'Create New Question'}</DialogTitle>
          <DialogDescription>
            {question
              ? 'Update question details and configuration'
              : 'Add a new question to the question bank'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Basic Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="question_text">Question Text *</Label>
                <Textarea
                  id="question_text"
                  value={formData.question_text}
                  onChange={(e) => setFormData({ ...formData, question_text: e.target.value })}
                  placeholder="Enter the question text..."
                  rows={3}
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  {/* Code is auto-generated server-side on create. On edit
                      we render the existing code as read-only context so
                      admins can still see what it is for reference / search. */}
                  <Label htmlFor="question_code">Code</Label>
                  {question ? (
                    <Input
                      id="question_code"
                      value={formData.question_code}
                      disabled
                      className="font-mono text-xs bg-slate-50 text-slate-600"
                    />
                  ) : (
                    <div
                      id="question_code"
                      className="flex items-center h-10 px-3 rounded-md border border-dashed border-slate-300 bg-slate-50 text-xs text-slate-500 italic"
                    >
                      Auto-generated when you save
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="category">Category *</Label>
                  <Select
                    key={`category-${open}-${question?.id || 'new'}`}
                    value={formData.category}
                    defaultValue={formData.category}
                    onValueChange={(value) => {
                      setFormData({ ...formData, category: value as QuestionCategory })
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a category" />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map((cat) => (
                        <SelectItem key={cat.value} value={cat.value}>
                          {cat.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="question_type">Question Type *</Label>
                <Select
                  key={`type-${open}-${question?.id || 'new'}`}
                  value={formData.question_type}
                  defaultValue={formData.question_type}
                  onValueChange={(value) => {
                    setFormData({ ...formData, question_type: value as QuestionType })
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select question type" />
                  </SelectTrigger>
                  <SelectContent>
                    {QUESTION_TYPES.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        <div>
                          <div className="font-medium">{type.label}</div>
                          <div className="text-xs text-slate-500">{type.description}</div>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="helper_text">Helper Text (Optional)</Label>
                <Textarea
                  id="helper_text"
                  value={formData.helper_text || ''}
                  onChange={(e) => setFormData({ ...formData, helper_text: e.target.value })}
                  placeholder="Provide guidance for answering this question..."
                  rows={2}
                />
              </div>
            </CardContent>
          </Card>

          {/* Question Configuration */}
          {formData.question_type && formData.question_type !== 'TEXT' && formData.question_type !== 'YES_NO' && (
            <Card className="border-blue-200">
              <CardHeader className="bg-blue-50">
                <CardTitle className="text-base flex items-center gap-2">
                  <HelpCircle className="h-4 w-4 text-blue-600" />
                  Question Configuration
                </CardTitle>
                <CardDescription>
                  Configure options specific to {QUESTION_TYPES.find(t => t.value === formData.question_type)?.label}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4 pt-6">
                {formData.question_type === 'RATING_SCALE' && (
                  <>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Minimum</Label>
                        <Input
                          type="number"
                          min={1}
                          max={10}
                          value={ratingMin}
                          onChange={(e) => setRatingMin(parseInt(e.target.value))}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Maximum</Label>
                        <Input
                          type="number"
                          min={1}
                          max={10}
                          value={ratingMax}
                          onChange={(e) => setRatingMax(parseInt(e.target.value))}
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Scale Labels (Optional)</Label>
                      <div className="grid grid-cols-2 gap-2">
                        <Input
                          placeholder={`Label for ${ratingMin}`}
                          value={ratingLabels[ratingMin] || ''}
                          onChange={(e) => setRatingLabels({ ...ratingLabels, [ratingMin]: e.target.value })}
                        />
                        <Input
                          placeholder={`Label for ${ratingMax}`}
                          value={ratingLabels[ratingMax] || ''}
                          onChange={(e) => setRatingLabels({ ...ratingLabels, [ratingMax]: e.target.value })}
                        />
                      </div>
                    </div>
                  </>
                )}

                {['LIKERT', 'MULTIPLE_CHOICE', 'MULTI_SELECT', 'RANKING'].includes(formData.question_type!) && (
                  <>
                    <div className="space-y-2">
                      <Label>Options</Label>
                      <div className="flex gap-2">
                        <Input
                          placeholder="Add an option..."
                          value={newOption}
                          onChange={(e) => setNewOption(e.target.value)}
                          onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addOption())}
                        />
                        <Button type="button" onClick={addOption}>
                          <Plus className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    <div className="space-y-2">
                      {options.map((option, index) => (
                        <div key={index} className="flex items-center gap-2">
                          <Badge variant="outline" className="flex-1 justify-between">
                            {option}
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => removeOption(index)}
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </Badge>
                        </div>
                      ))}
                    </div>

                    {formData.question_type === 'MULTI_SELECT' && (
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Min Selections</Label>
                          <Input
                            type="number"
                            min={1}
                            value={minSelections}
                            onChange={(e) => setMinSelections(parseInt(e.target.value))}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Max Selections</Label>
                          <Input
                            type="number"
                            min={1}
                            value={maxSelections || ''}
                            onChange={(e) => setMaxSelections(e.target.value ? parseInt(e.target.value) : undefined)}
                            placeholder="Unlimited"
                          />
                        </div>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          )}

          {/* Applicability Rules */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Applicability Rules</CardTitle>
              <CardDescription>
                Define where this question can be used
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Feedback Contexts *</Label>
                <div className="grid grid-cols-2 gap-2">
                  {FEEDBACK_CONTEXTS.map((context) => (
                    <div key={context.value} className="flex items-center space-x-2">
                      <Checkbox
                        id={context.value}
                        checked={formData.applicable_feedback_contexts?.includes(context.value)}
                        onCheckedChange={() => toggleContext(context.value)}
                      />
                      <label htmlFor={context.value} className="text-sm cursor-pointer">
                        {context.label}
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="is_required"
                  checked={formData.is_required}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_required: checked as boolean })}
                />
                <label htmlFor="is_required" className="text-sm cursor-pointer">
                  Mark as required question in templates
                </label>
              </div>
            </CardContent>
          </Card>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Saving...' : question ? 'Update Question' : 'Create Question'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

export default QuestionFormDialog
